// Form field grid used by JobModal. Pulled out so JobModal stays
// under 150 lines. State is owned by the parent.
import { useEffect } from "react";
import { JOB_PRIORITIES } from "../../../../../../shared/types/production-schedule";
import type {
  JobCreateInputT,
  SolutionTypeDto,
  TeamMemberDto,
} from "../../../../../../shared/types/production-schedule";
import { useActiveJobStatuses } from "../components/JobStatusesProvider";
import { NominalHoursFields } from "./NominalHoursFields";

export type JobFormState = Required<
  Pick<JobCreateInputT, "name" | "status" | "priority">
> & {
  jobNumber: string;
  customer: string;
  contact: string;
  location: string;
  notes: string;
  solutionTypeId: number | null;
  assigneeId: number | null;
  startDate: string;
  dueDate: string;
  sharepointUrl: string;
  // Stored as a string so the controlled input round-trips blanks
  // ("" → no value yet); jobFormState parses to number | null on
  // submit.
  orderValue: string;
  // Nominal labour budget per stage — { stageKey: hours }, keyed by
  // live stage key plus WELD_NOMINAL_KEY for the collapsed Welding
  // field. Blank inputs drop their key rather than storing 0 so the
  // distributor can tell "not budgeted" from "budgeted at zero".
  nominalStageHours: Record<string, number>;
  // Simple View: one stage pipeline for the whole job. Toggled from
  // the modal header; persisted on Save.
  simpleMode: boolean;
};

interface Props {
  value: JobFormState;
  onChange: (patch: Partial<JobFormState>) => void;
  team: TeamMemberDto[];
  types: SolutionTypeDto[];
  onManageTypes(): void;
  // True when editing a saved job. Existing jobs may carry an
  // archived status (legitimate — archive only hides from new
  // picks); new jobs must not, so when the seed default is archived
  // we snap the form to the first active label rather than offering
  // the archived option.
  isExistingJob: boolean;
}

const numOrNull = (s: string): number | null => {
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

export function JobFormFields({ value, onChange, team, types, onManageTypes, isExistingJob }: Props) {
  const set = onChange;
  // Active statuses for the picker. Existing-job rule: if the job
  // already carries an archived label, prepend it so the user can
  // save without being forced to change status. New-job rule: never
  // offer an archived label — snap to the first active option if
  // the seed default ("New") has itself been archived.
  const activeStatuses = useActiveJobStatuses();
  const activeLabels = activeStatuses.map((s) => s.label);
  const statusInActive = value.status ? activeLabels.includes(value.status) : false;
  const statusOptions = !statusInActive && isExistingJob && value.status
    ? [value.status, ...activeLabels]
    : activeLabels;

  useEffect(() => {
    if (isExistingJob) return;
    if (statusInActive) return;
    const first = activeLabels[0];
    if (first && first !== value.status) set({ status: first });
  }, [isExistingJob, statusInActive, activeLabels, value.status, set]);

  return (
    <>
      <div className="modal-section">Job Details</div>
      <div className="form-grid">
        <div className="form-group">
          <label>Job Number</label>
          <input type="text" value={value.jobNumber} placeholder="auto"
            onChange={(e) => set({ jobNumber: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Priority</label>
          <select value={value.priority}
            onChange={(e) => set({ priority: e.target.value as JobFormState["priority"] })}>
            {JOB_PRIORITIES.map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <div className="form-grid full">
        <div className="form-group">
          <label>Job Name</label>
          <input type="text" value={value.name} placeholder="e.g. Warringal Hospital Fitout"
            onChange={(e) => set({ name: e.target.value })} />
        </div>
      </div>
      <div className="form-grid g3">
        <div className="form-group">
          <label>Customer</label>
          <input type="text" value={value.customer} placeholder="Customer name"
            onChange={(e) => set({ customer: e.target.value })} />
        </div>
        <div className="form-group">
          <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            Solution Type
            <button type="button" onClick={onManageTypes}
              style={{ background: "none", border: 0, fontSize: 10, color: "var(--bsp-blue)", cursor: "pointer", padding: 0, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              ✎ Manage
            </button>
          </label>
          <select value={value.solutionTypeId ?? ""}
            onChange={(e) => set({ solutionTypeId: numOrNull(e.target.value) })}>
            <option value="">—</option>
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Order Value (AUD)</label>
          <div className="order-value-input">
            <span className="ov-prefix">$</span>
            <input
              type="number"
              value={value.orderValue}
              placeholder="0"
              min={0}
              step={100}
              inputMode="decimal"
              onChange={(e) => set({ orderValue: e.target.value })}
            />
          </div>
        </div>
      </div>
      <div className="form-grid g3">
        <div className="form-group">
          <label>Status</label>
          <select value={value.status}
            onChange={(e) => set({ status: e.target.value as JobFormState["status"] })}>
            {statusOptions.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Start Date</label>
          <input type="date" value={value.startDate}
            onChange={(e) => set({ startDate: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Due Date</label>
          <input type="date" value={value.dueDate}
            onChange={(e) => set({ dueDate: e.target.value })} />
        </div>
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label>Site Contact</label>
          <input
            type="text"
            value={value.contact}
            placeholder="Name · phone"
            onChange={(e) => set({ contact: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Site Location</label>
          <input
            type="text"
            value={value.location}
            placeholder="Site / area / level"
            onChange={(e) => set({ location: e.target.value })}
          />
        </div>
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label>Assignee</label>
          <select value={value.assigneeId ?? ""}
            onChange={(e) => set({ assigneeId: numOrNull(e.target.value) })}>
            <option value="">Unassigned</option>
            {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
      </div>
      <div className="form-grid full">
        <div className="form-group">
          <label>SharePoint URL</label>
          <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
            <input
              type="url"
              value={value.sharepointUrl}
              placeholder="https://yourcompany.sharepoint.com/sites/projects/…"
              onChange={(e) => set({ sharepointUrl: e.target.value })}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="btn-sm"
              disabled={!isValidUrl(value.sharepointUrl)}
              onClick={() => {
                if (isValidUrl(value.sharepointUrl)) {
                  window.open(value.sharepointUrl, "_blank", "noopener");
                }
              }}
              title={isValidUrl(value.sharepointUrl) ? "Open in a new tab" : "Enter a valid URL first"}
              style={{ padding: "8px 12px" }}
            >
              ↗ Open
            </button>
          </div>
        </div>
      </div>
      <div className="form-grid full">
        <div className="form-group">
          <label>Notes</label>
          <textarea value={value.notes} placeholder="Job notes…"
            onChange={(e) => set({ notes: e.target.value })} />
        </div>
      </div>
      <NominalHoursFields
        value={value.nominalStageHours}
        onChange={(nominalStageHours) => set({ nominalStageHours })}
      />
    </>
  );
}

function isValidUrl(s: string): boolean {
  if (!s || !s.trim()) return false;
  try { new URL(s); return true; } catch { return false; }
}
